import * as i0 from '@angular/core';
import { inject, NgZone, Injectable, ViewContainerRef, ElementRef, Directive, Input, Pipe, NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Overlay, ScrollDispatcher } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { TooltipComponent } from '@angular/material/tooltip';
import { ComponentStore } from '@ngrx/component-store';
import { withLatestFrom, map, filter, concatAll, concatMap, of, take, tap, delay } from 'rxjs';

class TutorialService extends ComponentStore {
    constructor() {
        super({
            tutorials: {},
        });
        this.overlay = inject(Overlay);
        this.scrollDispatcher = inject(ScrollDispatcher);
        this.ngZone = inject(NgZone);
        this.getTutorials$ = this.select(state => state.tutorials);
        this.addTutorialNode = this.updater((state, node) => {
            if (!(node.id in state.tutorials)) {
                state.tutorials[node.id] = {};
            }
            return {
                ...state,
                tutorials: {
                    ...state.tutorials,
                    [node.id]: {
                        ...state.tutorials[node.id],
                        [node.step]: node,
                    },
                },
            };
        });
        this.cleanupTutorial$ = this.updater((state, id) => {
            return {
                ...state,
                tutorials: {
                    ...state.tutorials,
                    [id]: {},
                },
            };
        });
        this.startTutorial = this.effect((trigger$) => trigger$.pipe(withLatestFrom(this.getTutorials$), map(([{ id }, tutorials]) => tutorials[id]), map(nodes => {
            const nodesInOrder = Object.values(nodes).sort((a, b) => a.step - b.step);
            return nodesInOrder;
        }), filter(nodes => nodes?.length > 0), concatAll(), concatMap(node => of(node).pipe(
        // tooltip creation adapted from
        // https://github.com/angular/components/blob/796638b62e7d5c69d4777930e3c2ce626c3d32b3/src/material/tooltip/tooltip.ts#L463
        map(node => {
            const { overlayRef } = this.createOverlay(node);
            const matTooltip = this.createTutorialTooltip(overlayRef, node);
            // TODO: figure out how to handle outside click event cancellations
            // this.handleTutorialTooltipDetachments(overlayRef, matTooltip.instance, node);
            // position must be updated after instance paints message to position after accounting
            // for size of text in tooltip.
            matTooltip.instance._markForCheck();
            this.ngZone.onMicrotaskEmpty.pipe(take(1)).subscribe(() => {
                if (matTooltip.instance) {
                    overlayRef.updatePosition();
                }
            });
            return { tooltip: matTooltip.instance, overlayRef };
        }), tap(({ tooltip }) => {
            tooltip.show(0);
        }), delay(node.displayTime), tap(({ tooltip, overlayRef }) => {
            this.completeTutorialStep(overlayRef, tooltip);
        })))));
    }
    mapPosition(node) {
        return node.position === 'above'
            ? {
                originX: 'center',
                originY: 'top',
                overlayX: 'center',
                overlayY: 'bottom',
            }
            : node.position === 'below'
                ? {
                    originX: 'center',
                    originY: 'bottom',
                    overlayX: 'center',
                    overlayY: 'top',
                }
                : node.position === 'right'
                    ? {
                        originX: 'end',
                        originY: 'center',
                        overlayX: 'start',
                        overlayY: 'center',
                    }
                    : node.position === 'left'
                        ? {
                            originX: 'start',
                            originY: 'center',
                            overlayX: 'end',
                            overlayY: 'center',
                        }
                        : { originX: 'center', originY: 'top', overlayX: 'center', overlayY: 'bottom' };
    }
    createOverlay(node) {
        const relativePosition = this.mapPosition(node);
        const scrollAncestors = this.scrollDispatcher.getAncestorScrollContainers(node.elementRef);
        const positionStrategy = this.overlay
            .position()
            .flexibleConnectedTo(node.elementRef)
            .withTransformOriginOn(`.mat-mdc-tooltip`)
            .withFlexibleDimensions(false)
            .withViewportMargin(8)
            .withScrollableContainers(scrollAncestors)
            .withPositions([
            {
                ...relativePosition,
                ...(node.offsetX && node.offsetY
                    ? [
                        {
                            offsetX: node.offsetX,
                            offsetY: node.offsetY,
                        },
                    ]
                    : []),
            },
        ]);
        const overlayRef = this.overlay.create({
            positionStrategy,
            hasBackdrop: true,
            backdropClass: 'btq-tutorial-overlay-backdrop',
            panelClass: 'overlay-pane',
            scrollStrategy: this.overlay.scrollStrategies.reposition({ autoClose: true, scrollThrottle: 20 }),
        });
        return { overlayRef, positionStrategy };
    }
    createTutorialTooltip(overlayRef, node) {
        const matTooltipPortal = new ComponentPortal(TooltipComponent, node.viewContainerRef);
        const matTooltip = overlayRef.attach(matTooltipPortal);
        matTooltip.instance._triggerElement = node.elementRef.nativeElement;
        matTooltip.instance.message = node.message;
        matTooltip.instance.tooltipClass = node.position ?? 'above';
        return matTooltip;
    }
    completeTutorialStep(overlayRef, tooltip) {
        if (overlayRef.hasAttached()) {
            overlayRef.detach();
        }
        if (tooltip.isVisible()) {
            tooltip.hide(0);
        }
    }
    // TODO: Figure out how to cancel or proceed to next step on oustide click
    // handleTutorialTooltipDetachments(overlayRef: OverlayRef, tooltip: TooltipComponent, node: TutorialNode): Subscription[] {
    //     const subscriptions: Subscription[] = [];
    //     subscriptions.push(
    //         of('')
    //             .pipe(delay(node.displayTime))
    //             .subscribe(() => {
    //                 if (tooltip.isVisible()) {
    //                     tooltip.hide(0);
    //                 }
    //                 if (overlayRef.hasAttached()) {
    //                     overlayRef.detach();
    //                 }
    //                 this.tutorialNodeComplete$.next(node.id);
    //             }),
    //         overlayRef.outsidePointerEvents().subscribe(() => {
    //             if (tooltip.isVisible()) {
    //                 tooltip.hide(0);
    //             }
    //             if (overlayRef.hasAttached()) {
    //                 overlayRef.detach();
    //             }
    //             this.tutorialNodeComplete$.next(node.id);
    //         }),
    //         overlayRef.keydownEvents().subscribe(event => {
    //             console.log('keydown');
    //             if (overlayRef.hasAttached()) {
    //                 overlayRef.detach();
    //             }
    //             if (tooltip.isVisible()) {
    //                 event.preventDefault();
    //                 event.stopPropagation();
    //                 tooltip.hide(0);
    //             }
    //             this.tutorialNodeComplete$.next(node.id);
    //         })
    //     );
    //     return [];
    // }
    cleanupSubscriptions(subscriptions) {
        subscriptions.forEach(subscription => {
            subscription.unsubscribe();
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.2", ngImport: i0, type: TutorialService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.0.2", ngImport: i0, type: TutorialService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.2", ngImport: i0, type: TutorialService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [] });

class TutorialNodeDirective {
    constructor() {
        this.tutorialService = inject(TutorialService);
        this.vcr = inject(ViewContainerRef);
        this.elRef = inject(ElementRef);
    }
    set titanTutorialNode(node) {
        this.id = node.id;
        this.message = node.message;
        this.step = node.step;
        this.position = node.position;
        this.displayTime = node.displayTime;
        this.offsetX = node.offsetX;
        this.offsetY = node.offsetY;
        this.tutorialService.addTutorialNode({
            viewContainerRef: this.vcr,
            elementRef: this.elRef,
            id: this.id,
            step: this.step,
            message: this.message,
            displayTime: this.displayTime ?? 3000,
            position: this.position,
            offsetX: this.offsetX ?? 0,
            offsetY: this.offsetY ?? 0,
        });
    }
    ngOnDestroy() {
        // cleaning up tutorial node so it is not duplicated on destroy and init
        this.tutorialService.cleanupTutorial$(this.id);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.2", ngImport: i0, type: TutorialNodeDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.0.2", type: TutorialNodeDirective, isStandalone: true, selector: "[titanTutorialNode]", inputs: { titanTutorialNode: "titanTutorialNode" }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.2", ngImport: i0, type: TutorialNodeDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[titanTutorialNode]',
                    standalone: true,
                }]
        }], propDecorators: { titanTutorialNode: [{
                type: Input
            }] } });

class TutorialTranslatePipe {
    transform(translatedMsg, node) {
        return {
            ...node,
            message: translatedMsg,
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.2", ngImport: i0, type: TutorialTranslatePipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "17.0.2", ngImport: i0, type: TutorialTranslatePipe, isStandalone: true, name: "tutorialNode" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.2", ngImport: i0, type: TutorialTranslatePipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'tutorialNode',
                    standalone: true,
                }]
        }] });

class TutorialsModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.2", ngImport: i0, type: TutorialsModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.0.2", ngImport: i0, type: TutorialsModule, imports: [CommonModule, TutorialNodeDirective, TutorialTranslatePipe], exports: [TutorialNodeDirective, TutorialTranslatePipe] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.0.2", ngImport: i0, type: TutorialsModule, imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.2", ngImport: i0, type: TutorialsModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, TutorialNodeDirective, TutorialTranslatePipe],
                    exports: [TutorialNodeDirective, TutorialTranslatePipe],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { TutorialNodeDirective, TutorialService, TutorialTranslatePipe, TutorialsModule };
